import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../lib/api';
import { fmtAED } from '../lib/format';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ShoppingCart, Plus, Minus, ChefHat, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

export default function QROrder() {
  const { tenantId, outletId, tableId } = useParams();
  const [data, setData] = useState(null);
  const [activeCat, setActiveCat] = useState('all');
  const [cart, setCart] = useState([]);
  const [placed, setPlaced] = useState(null);
  const [info, setInfo] = useState({ name: '', phone: '' });
  const [notes, setNotes] = useState('');

  useEffect(() => { (async () => { const res = await api.get('/public/menu', { params: { tenant_id: tenantId } }); setData(res.data); })(); }, [tenantId]);

  const items = useMemo(() => {
    if (!data) return [];
    let f = data.items;
    if (activeCat !== 'all') f = f.filter((i) => i.category_id === activeCat);
    return f;
  }, [data, activeCat]);

  const add = (item) => setCart((p) => { const ex = p.find((c) => c.menu_item_id === item.id); if (ex) return p.map((c) => c.menu_item_id === item.id ? { ...c, qty: c.qty + 1 } : c); return [...p, { menu_item_id: item.id, name: item.name, qty: 1, unit_price: item.price, station: item.station }]; });
  const chg = (id, d) => setCart((p) => p.map((c) => c.menu_item_id === id ? { ...c, qty: Math.max(0, c.qty + d) } : c).filter((c) => c.qty > 0));
  const total = cart.reduce((s, c) => s + c.unit_price * c.qty, 0) * 1.05;

  const place = async () => {
    try {
      const { data: res } = await api.post('/public/order', { tenant_id: tenantId, outlet_id: outletId || data.tenant?.id, table_id: tableId || null, items: cart, notes, customer_name: info.name, customer_phone: info.phone });
      setPlaced(res.order_no); setCart([]); setNotes('');
    } catch (e) { toast.error('Could not place order'); }
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading menu…</div>;
  if (placed) return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center bg-background">
      <CheckCircle2 className="h-16 w-16 text-success mb-4" />
      <h1 className="font-display text-2xl font-bold">Order Placed!</h1>
      <p className="text-muted-foreground mt-1">Your order <b>{placed}</b> is being prepared.</p>
      <Button className="mt-6" onClick={() => setPlaced(null)}>Order More</Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-background pb-32">
      <div className="hero-grain px-5 py-6">
        <div className="flex items-center gap-2"><div className="h-10 w-10 rounded-xl bg-primary flex items-center justify-center"><ChefHat className="h-5 w-5 text-primary-foreground" /></div>
          <div><div className="font-display font-bold text-xl">{data.tenant?.name}</div><div className="text-xs text-muted-foreground">Scan · Order · Enjoy {tableId ? `· Table` : ''}</div></div>
        </div>
      </div>
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b flex gap-2 overflow-x-auto no-scrollbar px-4 py-2">
        <button onClick={() => setActiveCat('all')} className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap border ${activeCat === 'all' ? 'bg-primary text-primary-foreground' : 'bg-card'}`}>All</button>
        {data.categories.map((c) => <button key={c.id} onClick={() => setActiveCat(c.id)} className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap border ${activeCat === c.id ? 'bg-primary text-primary-foreground' : 'bg-card'}`}>{c.name}</button>)}
      </div>
      <div className="p-4 space-y-2">
        {items.map((item) => {
          const inCart = cart.find((c) => c.menu_item_id === item.id);
          return (
            <div key={item.id} className="flex items-center gap-3 bg-card rounded-2xl border p-3" data-testid={`qr-item-${item.name}`}>
              <span className={`h-3 w-3 rounded-full shrink-0 ${item.veg ? 'bg-success' : 'bg-destructive'}`} />
              <div className="flex-1 min-w-0"><div className="font-medium text-sm">{item.name}</div><div className="font-mono text-sm text-muted-foreground">{fmtAED(item.price)}</div></div>
              {inCart ? (
                <div className="flex items-center gap-1"><Button size="icon" variant="outline" className="h-8 w-8" onClick={() => chg(item.id, -1)}><Minus className="h-3 w-3" /></Button><span className="w-6 text-center">{inCart.qty}</span><Button size="icon" className="h-8 w-8" onClick={() => chg(item.id, 1)}><Plus className="h-3 w-3" /></Button></div>
              ) : (
                <Button size="sm" data-testid={`qr-add-${item.name}`} onClick={() => add(item)}><Plus className="h-3 w-3 mr-1" />Add</Button>
              )}
            </div>
          );
        })}
      </div>
      {cart.length > 0 && (
        <div className="fixed bottom-0 inset-x-0 bg-card border-t p-4 pb-24 space-y-2">
          <div className="grid grid-cols-2 gap-2"><Input placeholder="Your name" value={info.name} onChange={(e) => setInfo({ ...info, name: e.target.value })} /><Input placeholder="Phone" value={info.phone} onChange={(e) => setInfo({ ...info, phone: e.target.value })} /></div>
          <Input data-testid="qr-notes" placeholder="Special instructions (allergies, no onions…)" value={notes} onChange={(e) => setNotes(e.target.value)} />
          <Button data-testid="qr-place-order" className="w-full h-12" onClick={place}><ShoppingCart className="h-4 w-4 mr-2" />Place Order · {fmtAED(total)}</Button>
        </div>
      )}
    </div>
  );
}
